import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Input, Textarea, VStack, Field, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useCreateSurveyGroup, useUpdateSurveyGroup } from '../../hooks/useSurvey'
import type { SaveSurveyGroupRequest, SurveyGroupSummaryDto } from '../../types/survey'

interface SurveyGroupFormModalProps {
  isOpen: boolean
  onClose: () => void
  group?: SurveyGroupSummaryDto | null
}

export function SurveyGroupFormModal({ isOpen, onClose, group }: SurveyGroupFormModalProps) {
  const isEdit = !!group
  const { register, handleSubmit, reset } = useForm<SaveSurveyGroupRequest>({
    defaultValues: {
      label: '',
      description: '',
    },
  })

  const createMutation = useCreateSurveyGroup()
  const updateMutation = useUpdateSurveyGroup(group?.id || '')

  useEffect(() => {
    if (group) {
      reset({
        label: group.label,
        description: group.description || '',
      })
    } else {
      reset({
        label: '',
        description: '',
      })
    }
  }, [group, reset])

  const onSubmit = async (data: SaveSurveyGroupRequest) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data)
        toaster.create({
          title: 'Group updated',
          description: 'The survey group has been updated successfully.',
          type: 'success',
          duration: 3000,
        })
      } else {
        await createMutation.mutateAsync(data)
        toaster.create({
          title: 'Group created',
          description: 'The survey group has been created successfully.',
          type: 'success',
          duration: 3000,
        })
      }
      onClose()
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Failed to ${isEdit ? 'update' : 'create'} group. Please try again.`,
        type: 'error',
        duration: 5000,
      })
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog.Root open={isOpen} size='lg' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Header>{isEdit ? 'Edit Survey Group' : 'Create Survey Group'}</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Label</Field.Label>
                    <Input size="sm" {...register('label', { required: true })} />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Description</Field.Label>
                    <Textarea size="sm" rows={3} {...register('description')} />
                  </Field.Root>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose} size="sm">
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  type="submit"
                  loading={isLoading}
                  size="sm"
                >
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
